import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { Contract } from './Contract'
import { Field, FieldConstructorArgs } from './Field'
import { Image } from './Image'
import { Option } from './Option'
import { FKType } from './SQLBuilder'
import { User } from './User'

export type TypeConstructorArgs = Omit<ConstructorArgs<() => Type>, 'fields'> & {
    fields: Field[] | FieldConstructorArgs[] | null
}

export class Type extends BaseRecord {
    readonly recordType = 'type'

    name?: string
    memo?: string
    key?: string
    image?: Image
    refnum?: string
    expires?: string
    edition?: string
    editionType?: string
    isPublic?: boolean
    alwaysRequire?: boolean
    associatedWithService?: boolean
    attachedContract?: string
    fields: Field[] = [] // YF: list of field instances for newly added forms
    selectedFields: Field[] = [] // list of field instances that are selected during onboarding
    cancelledDate?: moment.Moment
    lastModifiedDate?: moment.Moment

    /* transient data: used in types list for displaying drill downs for fields/records */
    fieldCount?: number

    constructor(data?: Partial<TypeConstructorArgs>, params?: { field_count?: number }) {
        super(data)
        if (!data && !params) {
            this.isPublic = true
            this.fields = []
            this.selectedFields = []
            this.owner = this.creator = PSApplication.me()
        }
        if (!!data && params?.field_count != undefined) {
            this.fieldCount = params.field_count
            this.fields = []
            this.selectedFields = []
            this.owner = this.creator = PSApplication.me()
        }
        if (!data) {
            return
        }

        this.key = data.key ?? undefined
        this.name = data.name ?? undefined
        this.memo = data.memo ?? undefined
        this.refnum = data.refnum ?? undefined
        this.expires = data.expires ?? undefined
        this.edition = data.edition ?? undefined
        this.editionType = data.edition_type ?? undefined
        this.isPublic = data.is_public ?? false
        this.alwaysRequire = data.always_require ?? false
        this.associatedWithService = data.associated_with_service ?? false
        this.attachedContract = data.attached_contract ?? undefined
        this.image = this.propertyFromImageArg(data.image, data.image_uri)
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
        this.fields = (data.fields ?? []).map((data) =>
            data instanceof Field ? data : new Field(data)
        )
        this.fieldCount = this.fields.length
    }

    clone(overrides?: Partial<TypeConstructorArgs>): Type {
        return new Type({ ...this.serializeForClone(), ...overrides })
    }

    get expiresString() {
        if (this.alwaysRequire) return 'always'
        if (this.expires) return this.expires
        return 'once'
    }

    static expirationStrings(): Option[] {
        return [
            new Option(undefined, {
                id: 'always',
                label: 'For each checkout',
                group: undefined,
                subtitle: 'Required every time the client checks out.',
            }),
            new Option(undefined, {
                id: 'once',
                label: 'During the first checkout',
                group: undefined,
                subtitle: 'Only required when the client first checks out.',
            }),
            new Option(undefined, { id: '1m', label: 'Every month' }),
            new Option(undefined, { id: '2m', label: 'Every 2 months' }),
            new Option(undefined, { id: '3m', label: 'Every 3 months' }),
            new Option(undefined, { id: '6m', label: 'Every 6 months' }),
            new Option(undefined, { id: '1y', label: 'Every 12 months' }),
            new Option(undefined, { id: '18m', label: 'Every 18 months' }),
            new Option(undefined, { id: '2y', label: 'Every 2 years' }),
            new Option(undefined, { id: '3y', label: 'Every 3 years' }),
        ]
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.creator) this.creator = BaseRecord.fromSerializer(data.creator, User)
        if (data.contract) this.contract = BaseRecord.fromSerializer(data.contract, Contract)
        if (data.image) this.image = BaseRecord.fromSerializer(data.image, Image)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
        if (data.fields)
            this.fields = data.fields.map((field: any) => BaseRecord.fromSerializer(field, Field))
        if (data.selectedFields)
            this.selectedFields = data.selectedFields.map((field: any) =>
                BaseRecord.fromSerializer(field, Field)
            )
    }

    static fk: FKType = {
        users: ['owner', 'creator'],
        contracts: ['contract'],
        images: ['image'],
    }

    static dataType(): DataType {
        return 'types'
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        dict['name'] = this.name
        dict['is_public'] = this.isPublic
        dict['always_require'] = this.alwaysRequire
        dict['memo'] = this.memo ?? ''
        dict['contract'] = this.contract ? this.contract.id : ''
        dict['expires'] = this.expires ?? ''
        dict['image'] = this.image ? this.image.id : ''
        if (this.edition) dict['edition'] = this.edition
        if (this.editionType) dict['edition_type'] = this.editionType
        if (this.fields.length > 0) {
            const fields = []
            for (const field of this.fields) fields.push(field.jsonObject())
            dict['fields'] = fields
        }
        return dict
    }

    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get creator(): User | undefined {
        return this.getForeignRecord('creator', User)
    }
    set creator(creator: User | undefined) {
        this.setForeignRecord('creator', creator)
    }
    get contract(): Contract | undefined {
        return this.getForeignRecord('contract', Contract)
    }
    set contract(contract: Contract | undefined) {
        this.setForeignRecord('contract', contract)
    }
}
